import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { BANNER_API_BASE_URL, getApiHeaders } from '../config/api';

type Banner = {
  _id: string;
  imageUrl: string;
  navUrl: string;
  navType: 'WEB' | 'SCREEN';
  priority: number;
  isActive: boolean;
  expiresAt?: string;
  title?: string;
  description?: string;
};


const BannerDetails: React.FC = () => {
  const { screen, sectionId, bannerId } = useParams<{ screen: string; sectionId: string; bannerId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    navUrl: '',
    navType: 'WEB' as 'WEB' | 'SCREEN',
    priority: 1,
    isActive: true,
    expiresAt: '',
    title: '',
    description: ''
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('wallet_assets_user_id');
    
    if (!token) return null;
    if (!userId) return null;
    
    const baseHeaders = getApiHeaders();
    return {
      'x-api-key': baseHeaders['x-api-key'],
      'userid': userId,
      'accesstoken': token,
    };
  }, []);

  const fetchBannerDetails = useCallback(async () => {
    if (!screen || !sectionId || !bannerId) return;
    
    const headers = headerWithAuth();
    if (!headers) {
      toast.error('Authentication failed');
      return;
    }

    setLoading(true);
    try {
      // Fetch all banners for the screen and find the specific one
      const res = await fetch(`${BANNER_API_BASE_URL}/banners/${screen}`, {
        method: 'GET',
        headers,
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to fetch banners');

      const screenData = json.data;
      // Handle both array (if fetching all) or single object (if fetching specific screen)
      const dataToSearch = Array.isArray(screenData) ? screenData.find((s: any) => s.screen === screen) : screenData;

      if (!dataToSearch) {
        throw new Error('Screen not found');
      }

      const section = dataToSearch.sections.find((s: any) => s._id === sectionId);
      if (!section) {
        throw new Error('Section not found');
      }

      const foundBanner = section.banners.find((b: any) => b._id === bannerId);
      if (!foundBanner) {
        throw new Error('Banner not found');
      }

      setBanner(foundBanner);
      setFormData({
        navUrl: foundBanner.navUrl || '',
        navType: foundBanner.navType || 'WEB',
        priority: foundBanner.priority || 1,
        isActive: foundBanner.isActive ?? true,
        expiresAt: foundBanner.expiresAt ? new Date(foundBanner.expiresAt).toISOString().slice(0, 16) : '',
        title: foundBanner.title || '',
        description: foundBanner.description || ''
      });
      setImagePreview(foundBanner.imageUrl);

    } catch (error) {
      toast.error((error as Error).message);
      navigate('/banners');
    } finally {
      setLoading(false);
    }
  }, [screen, sectionId, bannerId, headerWithAuth, navigate]);

  useEffect(() => {
    fetchBannerDetails();
  }, [fetchBannerDetails]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async () => {
    if (!screen || !sectionId || !bannerId) return;
    
    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const formPayload = new FormData();
      formPayload.append('navUrl', formData.navUrl);
      formPayload.append('navType', formData.navType);
      formPayload.append('priority', formData.priority.toString());
      formPayload.append('isActive', formData.isActive.toString());
      if (formData.expiresAt) formPayload.append('expiresAt', formData.expiresAt);
      if (formData.title) formPayload.append('title', formData.title);
      if (formData.description) formPayload.append('description', formData.description);
      
      if (imageFile) {
        formPayload.append('image', imageFile);
      }

      const res = await fetch(
        `${BANNER_API_BASE_URL}/banners/screen/${screen}/section/${sectionId}/banner/${bannerId}`,
        {
          method: 'PATCH',
          headers, // Do not set Content-Type for FormData, browser does it
          body: formPayload
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update banner');

      toast.success('Banner updated successfully');
      // Refresh details
      fetchBannerDetails();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this banner indefinitely?')) return;
    
    if (!screen || !sectionId || !bannerId) return;
    
    const headers = headerWithAuth();
    if (!headers) return;

    setSaving(true);
    try {
      const res = await fetch(
        `${BANNER_API_BASE_URL}/banners/screen/${screen}/section/${sectionId}/banner/${bannerId}`,
        {
          method: 'DELETE',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          }
        }
      );

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to delete banner');

      toast.success('Banner deleted');
      navigate('/banners');
    } catch (error) {
      toast.error((error as Error).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white'></div>
      </div>
    );
  }

  if (!banner) return null;

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900'>
      <Navbar />
      
      <div className='container mx-auto px-4 py-8'>
        {/* Header */}
        <div className='mb-8 flex items-center justify-between'>
          <div>
            <button
              onClick={() => navigate('/banners')}
              className='flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-4'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
              Back to Banners
            </button>
            <h1 className='text-3xl font-bold text-white'>Banner Details</h1>
            <p className='text-gray-400 mt-1'>
              {screen} &bull; Section {sectionId?.slice(-4)}
            </p>
          </div>
          <div className='flex gap-3'>
            <button
              onClick={handleDelete}
              disabled={saving}
              className='px-5 py-2.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-xl transition-all font-semibold disabled:opacity-50'
            >
              Delete Banner
            </button>
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
          {/* Left Column - Image & Preview */}
          <div className='lg:col-span-1 space-y-6'>
            <div className='bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl'>
              <h3 className='text-lg font-semibold text-white mb-4'>Banner Image</h3>
              <div className='relative aspect-video bg-gray-900 rounded-xl overflow-hidden mb-4 border border-white/10 group'>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt='Preview'
                    className='w-full h-full object-cover'
                  />
                ) : (
                  <div className='flex items-center justify-center h-full text-gray-500'>
                    No Image
                  </div>
                )}
                {/* Overlay upload button */}
                <div className='absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center'>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className='px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold transform translate-y-2 group-hover:translate-y-0 transition-all'
                  >
                    Change Image
                  </button>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                onChange={handleImageChange}
                className='hidden'
              />
              <p className='text-xs text-gray-400 text-center'>
                Click image to upload new version. Recommended size: 1920x1080px
              </p>
            </div>

            <div className='bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl'>
              <h3 className='text-lg font-semibold text-white mb-4'>Status</h3>
              <div className='flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10'>
                <div>
                  <p className='text-white font-medium'>Active Status</p>
                  <p className='text-sm text-gray-400'>
                    {formData.isActive ? 'Banner is visible' : 'Banner is hidden'}
                  </p>
                </div>
                <label className='relative inline-flex items-center cursor-pointer'>
                  <input
                    type='checkbox'
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className='sr-only peer'
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Right Column - Details Form */}
          <div className='lg:col-span-2'>
            <div className='bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-xl'>
              <h3 className='text-xl font-bold text-white mb-6'>Configuration</h3>
              
              <div className='space-y-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-2'>Navigation Type</label>
                    <select
                      value={formData.navType}
                      onChange={(e) => setFormData({ ...formData, navType: e.target.value as any })}
                      className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                    >
                      <option value='WEB'>Web URL</option>
                      <option value='SCREEN'>App Screen</option>
                    </select>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-2'>
                      {formData.navType === 'WEB' ? 'Destination URL' : 'Target Screen'}
                    </label>
                    
                      <input
                        type='text'
                        value={formData.navUrl}
                        onChange={(e) => setFormData({ ...formData, navUrl: e.target.value })}
                        placeholder='https://...'
                        className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500'
                      />  
                  </div>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-2'>Title (Optional)</label>
                    <input
                      type='text'
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500'
                    />
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-300 mb-2'>Priority</label>
                    <input
                      type='number'
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                    />
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Description (Optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-gray-300 mb-2'>Expiration Date</label>
                  <input
                    type='datetime-local'
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                    className='w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500'
                  />
                  <p className='text-xs text-gray-400 mt-1'>Leave empty for no expiration</p>
                </div>

                <div className='pt-6 border-t border-white/10 flex justify-end gap-3'>
                  <button
                    onClick={() => navigate('/banners')}
                    className='px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-xl font-semibold transition-all'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    disabled={saving}
                    className='px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all shadow-lg disabled:opacity-50'
                  >
                    {saving ? 'Saving Changes...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerDetails;
